// View state that the user can change lives in the URL (see modelview.ts), so the browser's Back button returns to the
// very same view. These are the pure helpers for the stage's lenses, the story, the peeks and the windows (urlmodal.ts).
// Every view switch is a history step (push), so Back undoes it; only typing, tidying the URL and continuous moves
// (the scrubber) replace the entry. Windows and peeks count the entries they pushed, so closing goes back past them.

/** Parameters with the changes applied (null / '' delete), everything else untouched. One call: setSearchParams(fn) does not chain. */
export function patchParams(p: URLSearchParams, changes: Record<string, string | null | undefined>): URLSearchParams {
  const out = new URLSearchParams(p);
  for (const [k, v] of Object.entries(changes)) {
    if (v === null || v === undefined || v === '') out.delete(k);
    else out.set(k, v);
  }
  return out;
}

/** `only=<cls>:<state>`: the records lens's problem chip (the records stuck at a step). */
export interface OnlyFilter { cls: string; state: 'missing' | 'pending' }

export const encodeOnly = (f: OnlyFilter | null): string | null => (f ? `${f.cls}:${f.state}` : null);

export function parseOnly(raw: string | null | undefined): OnlyFilter | null {
  if (!raw) return null;
  const i = raw.lastIndexOf(':');
  if (i <= 0) return null;
  const state = raw.slice(i + 1);
  return state === 'missing' || state === 'pending' ? { cls: raw.slice(0, i), state } : null;
}

/** A count of extra rows shown (`more=200`): a positive integer, else 0. */
export function parseCount(raw: string | null | undefined): number {
  const n = Number(raw);
  return raw && Number.isInteger(n) && n > 0 ? n : 0;
}

/** `lens=none`: the lens the stage opened by itself, closed by the user. */
export const LENS_NONE = 'none';

/** `story=1` is the story of the record the stage is about; `story=<cls>:<key>` that of a record opened in a peek. */
export type StoryParam = { kind: 'single' } | { kind: 'record'; cls: string; key: string };

export const encodeStory = (s: StoryParam | null): string | null => (!s ? null : s.kind === 'single' ? '1' : `${s.cls}:${s.key}`);

export function parseStory(raw: string | null | undefined): StoryParam | null {
  if (!raw) return null;
  if (raw === '1') return { kind: 'single' };
  const i = raw.indexOf(':');
  return i > 0 && i < raw.length - 1 ? { kind: 'record', cls: raw.slice(0, i), key: raw.slice(i + 1) } : null;
}

/** The peek's own view state, beside `peek`: the document's raw toggle, field filter and selected field; the record's "how fresh". */
export const PEEK_VIEW_KEYS = ['praw', 'pf', 'psel', 'pfresh'] as const;

/** The parameters without any peek view state: another peek on top starts clean. */
export function clearPeekView(p: URLSearchParams): URLSearchParams {
  const out = new URLSearchParams(p);
  for (const k of PEEK_VIEW_KEYS) out.delete(k);
  return out;
}

// ---- how a peek closes. Opening pushes an entry and counts them in the entry's state (`mvPeek`) — the pushes since
// the stage had no peek open — so closing can walk exactly that far back: Back then never reopens what was closed.
// A deep link (or a peek opened over one) has no such count and closes by replacing.

/** The count an opening push carries: 1 over a stage without a peek, one more than before otherwise (0 = unknown origin). */
export const pushedCount = (stackBefore: number, countBefore: number): number => (stackBefore === 0 ? 1 : countBefore > 0 ? countBefore + 1 : 0);

/** How many entries closing every peek goes back (0 = replace instead). */
export const closeSteps = (count: number): number => (Number.isInteger(count) && count > 0 ? count : 0);

/** Whether the breadcrumb back (one peek less) can be a history step: the entry before is exactly that stack. */
export const canStepBack = (count: number, stackLength: number): boolean => stackLength > 1 && count === stackLength;

export const readPeekCount = (state: unknown): number => {
  const n = (state as { mvPeek?: unknown } | null)?.mvPeek;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : 0;
};

/** How many history entries a window pushed (lib/urlmodal.ts): a count, or `true` from before steps were counted. */
export const modalSteps = (mark: unknown): number => (typeof mark === 'number' && Number.isInteger(mark) && mark > 0 ? mark : mark === true ? 1 : 0);
